import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const CACHE_KEY = "zyro_wishlist_ids_v1";

let sharedIds = new Set(
  typeof window !== "undefined" ? JSON.parse(localStorage.getItem(CACHE_KEY) || "[]") : []
);
const listeners = new Set();

function notify() {
  listeners.forEach((l) => l(new Set(sharedIds)));
  localStorage.setItem(CACHE_KEY, JSON.stringify([...sharedIds]));
}

export function useWishlist() {
  const [ids, setIds] = useState(sharedIds);

  useEffect(() => {
    listeners.add(setIds);
    return () => listeners.delete(setIds);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/wishlist");
      sharedIds = new Set([
        ...data.hotels.map((h) => `hotel:${h.id}`),
        ...data.properties.map((p) => `property:${p.id}`),
      ]);
      notify();
      return data;
    } catch {
      return null;
    }
  }, []);

  const toggle = useCallback(async (kind, itemId) => {
    const key = `${kind}:${itemId}`;
    const wasFav = sharedIds.has(key);
    // Optimistic
    if (wasFav) sharedIds.delete(key); else sharedIds.add(key);
    notify();
    try {
      if (wasFav) {
        await api.delete(`/wishlist/${kind}/${itemId}`);
        toast.success("Removed from wishlist");
      } else {
        await api.post(`/wishlist/${kind}/${itemId}`);
        toast.success("Added to wishlist ❤");
      }
    } catch (e) {
      // Revert
      if (wasFav) sharedIds.add(key); else sharedIds.delete(key);
      notify();
      if (e.response?.status === 401) {
        toast.error("Please log in to save favourites");
      } else {
        toast.error("Could not update wishlist");
      }
    }
  }, []);

  const has = useCallback((kind, itemId) => ids.has(`${kind}:${itemId}`), [ids]);

  return { ids, has, toggle, refresh };
}
